import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  IconButton,
  TextField,
  List, 
  ListItem, 
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Drawer,
  InputAdornment,
  Button,
  useTheme,
  styled,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemButton,
  Link,
  Menu,
  MenuItem,
  Card,
  CardContent,
} from '@mui/material';
import {
  Layers,
  MoreVert,
  Search,
  Home,
  Nature,
  Menu as MenuIcon,
  School,
  Close,
  FamilyRestroom,
  Man,
  TrendingUp,
  Public,
  ExpandMore,
  Groups,
  LocalHospital,
  Person,
  Grass,
  Spa,
  BugReport,
  Hive,
  Water as WaterIcon,
  Groups3,
  Handshake,
  Diversity3,
  House,
  Roofing,
  Map,
  Pets as LivestockIcon,
  EggAlt as PoultryIcon,
  Cottage as EmergencyHomeIcon, 
  AccessibilityNew, 
  ChildCare, 
  Terrain, 
  Engineering 
} from '@mui/icons-material';

// Constantes de style
const drawerWidth = 240;
const primaryColor = '#E30613';
const secondaryColor = '#008D36';
const backgroundColor = '#f5f5f5';

// Composants stylisés
const MainContainer = styled('div')({
  display: 'flex',
  backgroundColor: backgroundColor,
  minHeight: '100vh',
  flexDirection: 'column'
});

const MainContentWithDrawer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  marginTop: '64px',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    marginTop: '56px'
  }
}));

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  backgroundColor: backgroundColor,
  height: 'calc(100vh - 64px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    height: 'calc(100vh - 56px)'
  }
}));

const DrawerWrapper = styled('div')(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  overflow: 'auto',
  height: 'calc(100vh - 64px)',
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  backgroundColor: '#fff',
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    zIndex: 1300,
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    '&.drawerOpen': {
      transform: 'translateX(0)',
    },
    '&.drawerClose': {
      transform: 'translateX(-100%)',
    },
  }
}));

const MobileDrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: primaryColor,
  '& th': {
    color: 'white',
    fontWeight: 'bold'
  }
});

const ProvinceRow = styled(TableRow)({
  backgroundColor: '#e0e0e0',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#d0d0d0 !important'
  }
});

const menuItems = [
  { text: 'Ménages par type', icon: <FamilyRestroom color="primary" />, to: '/RGPHAEDataPage' },
  { text: 'Population par localisation', icon: <Public color="primary" />, to: '/PopulationDataPage' },
  { text: 'Ménages collectifs', icon: <Groups color="primary" />, to: '/PopulationCollectiveHouseholdsPage' },
  { text: 'Sans-abris', icon: <EmergencyHomeIcon color="primary" />, to: '/HomelessPopulationPage' },
  { text: 'Ménages ordinaires', icon: <Home color="primary" />, to: '/PopulationResidentPage' },
  { text: 'Sexe et masculinité', icon: <Man color="primary" />, to: '/PopulationDataPageM' },
  { text: 'Rapport de masculinité', icon: <TrendingUp color="primary" />, to: '/MasculinityRatioPage' },
  { text: 'Population par âge', icon: <ChildCare color="primary" />, to: '/PopulationDataPageAge' },
  { text: "Grands groupes d'âge", icon: <AccessibilityNew color="primary" />, to: '/PopulationAgeGroupPageGrand' },
  { text: "Groupes d'âge (éducation)", icon: <School color="primary" />, to: '/PopulationByAgeGroupPage' },
  { text: 'Groupes d\'âge (santé)', icon: <LocalHospital color="primary" />, to: '/AgeGroupPopulationPage' },
  { text: 'Adultes (18+)', icon: <Person color="primary" />, to: '/AdultPopulationPage' },
  
  // Agriculture
  { text: 'Ménages agricoles', icon: <Nature color="primary" />, to: '/AgriculturalHouseholdsPage' },
  { text: 'Pratiques agricoles', icon: <Grass color="primary" />, to: '/AgriculturalHouseholdsPages' },
  { text: 'Types de cultures', icon: <Spa color="primary" />, to: '/AgriculturalHouseholdsPage20' },
  
  // Élevage
  { text: 'Élevage par espèces', icon: <LivestockIcon color="primary" />, to: '/FarmingHouseholdsPage' },
  { text: 'Élevage de volailles', icon: <PoultryIcon color="primary" />, to: '/poultryFarmingPage' },
  { text: 'Élevage spécial', icon: <BugReport color="primary" />, to: '/insectFarmingHouseholdsPage' },
  { text: 'Répartition cheptel', icon: <LivestockIcon color="primary" />, to: '/livestockDistributionPage' },
  { text: 'Répartition volailles', icon: <PoultryIcon color="primary" />, to: '/poultryDistributionPage' },
  
  // Matériel agricole
  { text: 'Équipements agricoles', icon: <Engineering color="primary" />, to: '/agriculturalEquipmentPage' },
  { text: 'Apiculture', icon: <Hive color="primary" />, to: '/beekeepingHouseholdsPage' },
  { text: 'Pêche/aquaculture', icon: <WaterIcon color="primary" />, to: '/fishingHouseholdsPage' },
  
  // Associations
  { text: 'Interventions associations', icon: <Groups3 color="primary" />, to: '/associationInterventionPage' },
  { text: 'Interventions coopératives', icon: <Handshake color="primary" />, to: '/cooperativeInterventionPage' },
  { text: 'Interventions ONG', icon: <Diversity3 color="primary" />, to: '/interventionProjectsPage' },
  
  // Habitat
  { text: "Type d'habitat", icon: <House color="primary" />, to: '/housingDistributionPage' },
  { text: 'Matériau toit', icon: <Roofing color="primary" />, to: '/roofMaterialPage' },
  { text: 'Matériau sol', icon: <Layers color="primary" />, to: '/housingMaterialPage' },
  { text: 'Ménages agricoles', icon: <Terrain color="primary" />, to: '/agriculturalHouseholdsPageProvCom' },
  { text: 'Population par âge/résidence', icon: <Map color="primary" />, to: '/populationAgeResidencePage' },
];

// Structure des données
const poultryData = {
  title: "Nombre de ménages agricoles pratiquant l'élevage des volailles par province selon les espèces",
  species: ["Poule de race locale", "Poule de race améliorée", "Pintade", "Canard", "Dindon", "Caille", "Pigeon", "Oie"],
  provinces: [
    {
      name: "BUHUMUZA",
      "Poule de race locale": "104 457",
      "Poule de race améliorée": "6 133",
      "Pintade": "404",
      "Canard": "12 782",
      "Dindon": "95",
      "Caille": "107",
      "Pigeon": "1 263",
      "Oie": "314",
      total: "114 875"
    },
    {
      name: "BUJUMBURA",
      "Poule de race locale": "96 412",
      "Poule de race améliorée": "13 376",
      "Pintade": "764",
      "Canard": "14 171",
      "Dindon": "312",
      "Caille": "121",
      "Pigeon": "2 247",
      "Oie": "590",
      total: "111 906"
    },
    {
      name: "BURUNGA",
      "Poule de race locale": "125 743",
      "Poule de race améliorée": "7 306",
      "Pintade": "630",
      "Canard": "5 623",
      "Dindon": "93",
      "Caille": "101",
      "Pigeon": "2 483",
      "Oie": "211",
      total: "132 479"
    },
    {
      name: "BUTANYERERA",
      "Poule de race locale": "76 323",
      "Poule de race améliorée": "11 544",
      "Pintade": "198",
      "Canard": "9 623",
      "Dindon": "114",
      "Caille": "125",
      "Pigeon": "450",
      "Oie": "389",
      total: "89 022"
    },
    {
      name: "GITEGA",
      "Poule de race locale": "105 817",
      "Poule de race améliorée": "13 286",
      "Pintade": "268",
      "Canard": "2 602",
      "Dindon": "113",
      "Caille": "118",
      "Pigeon": "182",
      "Oie": "374",
      total: "114 992"
    }
  ],
  total: {
    "Poule de race locale": "508 752",
    "Poule de race améliorée": "51 645",
    "Pintade": "2 264",
    "Canard": "44 801",
    "Dindon": "727",
    "Caille": "572",
    "Pigeon": "6 625",
    "Oie": "1 878",
    total: "563 274"
  }
};

const PoultryFarmingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [openKeyStats, setOpenKeyStats] = useState(true);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Calcul des indicateurs clés
  const keyStats = {
    totalHouseholds: poultryData.total.total,
    
    mostCommonSpecies: poultryData.species.reduce((max, species) => {
      const currentTotal = parseInt(poultryData.total[species].replace(/\s/g, ''));
      const maxTotal = parseInt(poultryData.total[max].replace(/\s/g, ''));
      return currentTotal > maxTotal ? species : max;
    }, poultryData.species[0]),
    
    provinceWithMostPoultry: poultryData.provinces.reduce((max, province) => 
      parseInt(province.total.replace(/\s/g, '')) > parseInt(max.total.replace(/\s/g, '')) ? province : max
    ),
    
    provinceWithLeastPoultry: poultryData.provinces.reduce((min, province) => 
      parseInt(province.total.replace(/\s/g, '')) < parseInt(min.total.replace(/\s/g, '')) ? province : min
    ),
    
    highestLocalChickenPercentage: poultryData.provinces.reduce((max, province) => {
      const percentage = (parseInt(province["Poule de race locale"].replace(/\s/g, '')) / parseInt(province.total.replace(/\s/g, ''))) * 100;
      return percentage > max.percentage ? { province, percentage } : max;
    }, { province: null, percentage: 0 }),
    
    highestImprovedChickenPercentage: poultryData.provinces.reduce((max, province) => {
      const percentage = (parseInt(province["Poule de race améliorée"].replace(/\s/g, '')) / parseInt(province.total.replace(/\s/g, ''))) * 100;
      return percentage > max.percentage ? { province, percentage } : max;
    }, { province: null, percentage: 0 })
  };

  const filteredProvinces = poultryData.provinces.filter(province =>
    !searchTerm || province.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Composant du menu latéral
  const drawer = (
    <div>
      {isMobile && (
        <MobileDrawerHeader>
          <IconButton onClick={handleDrawerToggle}>
            <Close color="primary" />
          </IconButton>
        </MobileDrawerHeader>
      )}
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.to}
              onClick={isMobile ? handleDrawerToggle : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <MainContainer>
      <CssBaseline />
      <NavigationBar />
      <MainContentWithDrawer>
        {!isMobile ? (
          <DrawerWrapper>
            {drawer}
          </DrawerWrapper>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
        )}

        <ContentWrapper>
          <Container maxWidth="lg">
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<MenuIcon />} 
                  onClick={handleDrawerToggle}
                  sx={{ color: primaryColor, borderColor: primaryColor }}
                >
                  Menu
                </Button>
              </Box>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 1, sm: 3 }, mb: 3, borderLeft: `4px solid ${primaryColor}` }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2 }}>
                    <Typography variant="h6" component="h2" fontWeight="bold" color="primary" sx={{ mb: { xs: 1, sm: 0 } }}>
                      {poultryData.title}
                    </Typography>
                  </Box>
                  <Grid container spacing={1} style={{ marginTop: '5px',marginBottom: '5px' }}>
                    <Grid item xs={12} sm={6} md={6} ></Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        size="small"
                        placeholder="Rechercher province..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: { xs: '100%', sm: '480px' } }}
                      />
                    </Grid>
                  </Grid>
    
                  {/* Section des indicateurs clés */}
                  <Accordion expanded={openKeyStats} onChange={() => setOpenKeyStats(!openKeyStats)} sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Indicateurs Clés par Province
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Ménages Éleveurs Totaux
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {poultryData.total.total}
                              </Typography>
                              <Typography variant="caption">
                                pratiquant l'élevage de volailles
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Espèce la plus commune
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.mostCommonSpecies}
                              </Typography>
                              <Typography variant="body1">
                                {poultryData.total[keyStats.mostCommonSpecies]}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Province avec plus d'élevage
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.provinceWithMostPoultry.name}
                              </Typography>
                              <Typography variant="body1">
                                {keyStats.provinceWithMostPoultry.total}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Plus forte proportion poules locales
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.highestLocalChickenPercentage.province.name}
                              </Typography>
                              <Typography variant="body1">
                                {keyStats.highestLocalChickenPercentage.percentage.toFixed(1)}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  
                  <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
                    <Table size="small" aria-label="Tableau des ménages agricoles pratiquant l'élevage des volailles">
                      <StyledTableHead>
                        <TableRow>
                          <TableCell>Province</TableCell>
                          {poultryData.species.map((species, index) => (
                            <TableCell key={index} align="right">{species}</TableCell>
                          ))}
                          <TableCell align="right">Total ménages éleveurs</TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        {/* Ligne pour le total Burundi */}
                        <ProvinceRow hover>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            BURUNDI
                          </TableCell>
                          {poultryData.species.map((species, index) => (
                            <TableCell key={index} align="right" sx={{ fontWeight: 'bold' }}>
                              {poultryData.total[species]}
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{poultryData.total.total}</TableCell>
                        </ProvinceRow>

                        {/* Données par province */}
                        {filteredProvinces.map((province, index) => (
                          <ProvinceRow key={index} hover>
                            <TableCell component="th" scope="row">
                              {province.name}
                            </TableCell>
                            {poultryData.species.map((species, idx) => (
                              <TableCell key={idx} align="right">{province[species]}</TableCell>
                            ))}
                            <TableCell align="right">{province.total}</TableCell>
                          </ProvinceRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer> 
                </Paper>
                
                {/* Section d'information */}
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" color="primary">
                      À propos de l'élevage de volailles
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" paragraph>
                      L'élevage de volailles est une activité importante pour les ménages agricoles burundais, fournissant :
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      <ListItem>
                        <ListItemText 
                          primary="• Une source de protéines animales" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• Un revenu complémentaire" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• Des œufs pour la consommation et la vente" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• Des engrais naturels pour les cultures" 
                        />
                      </ListItem>
                    </List>
                    <Typography variant="body2" mt={2}>
                      Ces données représentent {poultryData.total.total} ménages agricoles pratiquant l'élevage de volailles au Burundi, 
                      avec une prédominance de l'élevage de {keyStats.mostCommonSpecies.toLowerCase()} ({poultryData.total[keyStats.mostCommonSpecies]} ménages).
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                {/* Notes méthodologiques */}
                <Paper sx={{ p: 2, backgroundColor: '#f0f0f0', borderLeft: `4px solid ${secondaryColor}` }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Notes méthodologiques
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les données présentées sont issues du Recensement Général de la Population, de l'Habitat, de l'Agriculture et de l'Elevage (RGPHAE) 
                    réalisé en 2024. Un ménage agricole est comptabilisé comme pratiquant l'élevage de volailles s'il possède au moins une volaille 
                    d'une espèce donnée. Les chiffres représentent le nombre de ménages, pas le nombre de volailles.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </ContentWrapper>
      </MainContentWithDrawer>
      
      {/* Pied de page */}
      <Footer />
    </MainContainer>
  );
};

export default PoultryFarmingPage;